import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useMatch } from 'react-router';
import { ArrowLeft, ClipboardList, MessagesSquare, Users } from 'lucide-react';

import {
  STUDENT_TUTOR_SORT_PRESETS,
  type StudentTutorSortPreset,
} from 'hooks/useStudentTutorSearch';
import { useBulletin } from 'hooks/useBulletin';
import { useAuthSession } from 'hooks/useAuthSession';
import { useUpdateBulletinActive } from 'hooks/useUpdateBulletinActive';
import { parsePositiveInt } from 'utils/parsePositiveInt';
import { useStudentTutorSortOptional } from '../StudentTutorSortContext';

import styles from './DetailsMenu.module.scss';
import { useParams } from 'react-router';

const Menu = () => {
  const { id } = useParams();

  return (
    <menu className={styles.menu}>
      <Link to='/profile/tasks' className={styles['back-link']}>
        <ArrowLeft size={18} aria-hidden />
        <p>Назад к задачам</p>
      </Link>
      <div className={styles['menu-nav']}>
        <NavLink
          to={`/profile/details/task/${id}`}
          end
          className={({ isActive }) => (isActive ? styles.active : undefined)}
        >
          <ClipboardList size={18} aria-hidden />
          <p>Детали задачи</p>
        </NavLink>
        <NavLink
          to={`/profile/details/${id}/chats`}
          end
          className={({ isActive }) => (isActive ? styles.active : undefined)}
        >
          <MessagesSquare size={18} aria-hidden />
          <p>Отклики и чаты</p>
        </NavLink>
        <NavLink
          to={`/profile/details/${id}/search`}
          end
          className={({ isActive }) => (isActive ? styles.active : undefined)}
        >
          <Users size={18} aria-hidden />
          <p>Специалисты</p>
        </NavLink>
      </div>
    </menu>
  );
};

const SwitchNotification = () => {
  const { id } = useParams();

  const { data: bulletin } = useBulletin(parsePositiveInt(id) ?? 0);

  const updateActive = useUpdateBulletinActive();
  const { data: authSession } = useAuthSession();

  if (!bulletin) return null;
  // Закрытая/завершённая задача (контакты переданы или поиск завершён): переключать нечего - панель убираем.
  if (bulletin.closed) return null;
  // Чужая задача по подстановке id: управлять откликами может только автор.
  const ownerId = (bulletin as { id_user?: number }).id_user;
  const meId = (authSession as { id?: number } | null | undefined)?.id;
  if (ownerId && meId && ownerId !== meId) return null;

  const notApproved =
    Boolean(bulletin.verification_status) &&
    bulletin.verification_status !== 'approved';

  const handleToggle = (value: boolean) => {
    if (notApproved) return;
    updateActive.mutate({
      id: bulletin.id,
      active: value,
    });
  };

  return (
    <div className={styles['switch-notification']}>
      <h3>
        {notApproved
          ? bulletin.verification_status === 'rejected'
            ? 'Заявка отклонена'
            : bulletin.verification_status === 'changes_requested'
              ? 'Нужны правки'
              : 'Заявка на модерации'
          : bulletin.active
            ? 'Отклики включены'
            : 'Отклики отключены'}
      </h3>

      <p>
        {notApproved
          ? 'Пока заявка не одобрена, репетиторы ее не видят - отклики и приглашения недоступны.'
          : bulletin.active
            ? 'Специалисты видят задачу и могут писать вам.'
            : 'Специалисты не видят задачу и не могут писать вам.'}
      </p>

      <div className={styles['radio-group']}>
        <label className={styles['radio-label']}>
          <input
            type='radio'
            name='bulletin-active'
            checked={bulletin.active === true}
            disabled={notApproved || updateActive.isPending}
            onChange={() => handleToggle(true)}
          />
          <span className={styles['radio-custom']}></span>
          <span className={styles['radio-text']}>Включены</span>
        </label>

        <label className={styles['radio-label']}>
          <input
            type='radio'
            name='bulletin-active'
            checked={bulletin.active === false}
            disabled={notApproved || updateActive.isPending}
            onChange={() => handleToggle(false)}
          />
          <span className={styles['radio-custom']}></span>
          <span className={styles['radio-text']}>Выключены</span>
        </label>
      </div>
    </div>
  );
};

const SpecialistsSortCard = () => {
  const sortCtx = useStudentTutorSortOptional();
  if (!sortCtx) return null;

  const { sortPreset, setSortPreset, resetSort } = sortCtx;

  const selectPreset = (id: Exclude<StudentTutorSortPreset, null>) => {
    setSortPreset(id);
  };

  return (
    <div className={styles['sort-card']} aria-label='Сортировка специалистов'>
      <h3>Сортировка</h3>
      <div className={styles['sort-options']} role='radiogroup' aria-label='Сортировка'>
        {STUDENT_TUTOR_SORT_PRESETS.map((preset) => (
          <label key={preset.id} className={styles['sort-option']}>
            <input
              type='radio'
              name='specialists-sort'
              checked={sortPreset === preset.id}
              onChange={() => selectPreset(preset.id)}
            />
            <span className={styles['sort-radio']} />
            <span className={styles['sort-label']}>{preset.label}</span>
          </label>
        ))}
        <label className={styles['sort-option']}>
          <input
            type='radio'
            name='specialists-sort'
            checked={sortPreset === null}
            onChange={resetSort}
          />
          <span className={styles['sort-radio']} />
          <span className={styles['sort-label']}>Сброс</span>
        </label>
      </div>
    </div>
  );
};

// На мобильном (до 899px) меню задачи стоит после хлебных крошек и заголовка
// страницы: порядок крошки → заголовок → вкладки → контент. Страница
// ставит <DetailsMenuSlot /> сразу после <h1>, и меню переносится туда порталом.
// На десктопе меню остается слева, как в макете.
const MOBILE_QUERY = '(max-width: 899px)';
let menuSlot: HTMLElement | null = null;
const slotListeners = new Set<() => void>();
const setMenuSlot = (el: HTMLElement | null) => {
  menuSlot = el;
  slotListeners.forEach((listener) => listener());
};
const subscribeSlot = (listener: () => void) => {
  slotListeners.add(listener);
  return () => {
    slotListeners.delete(listener);
  };
};
const subscribeMobile = (listener: () => void) => {
  const mq = window.matchMedia(MOBILE_QUERY);
  mq.addEventListener('change', listener);
  return () => mq.removeEventListener('change', listener);
};

export const DetailsMenuSlot = () => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    setMenuSlot(el);
    return () => {
      if (menuSlot === el) setMenuSlot(null);
    };
  }, []);
  return <div ref={ref} className={styles['menu-slot']} />;
};

export const DetailsMenu = () => {
  const specialistsMatch = useMatch('/profile/details/:id/search');
  const slot = useSyncExternalStore(subscribeSlot, () => menuSlot);
  const isMobile = useSyncExternalStore(
    subscribeMobile,
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false,
  );

  const content = (
    <div className={styles['menu-wrapper']}>
      <Menu />
      <SwitchNotification />
      {specialistsMatch ? <SpecialistsSortCard /> : null}
    </div>
  );

  if (isMobile && slot) return createPortal(content, slot);
  return content;
};
